import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { useDataSource } from "@/adapters/data-source";
import { DateRangePicker } from "@/components/dates/DateRangePicker";
import { EmptyState, LoadFailed } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { Label } from "@/components/ui/label";
import { gstDefaultPeriod } from "@/lib/gst-period";
import { formatMoney } from "@/modules/gst/format";
import { aggregateHsnSummary } from "@/modules/gst/hsnSummary";

/**
 * Taxable value and tax aggregated by HSN code, for a period (FR-GST-14,
 * ticket #92).
 *
 * A GSTR-1 return is filed by HSN code, not by invoice, because two rates
 * routinely sit inside one filing period here — imitation jewellery at 3%
 * and wristwatches at 18% — and a return cannot be filed off a list of
 * invoices without doing this grouping by hand first. `aggregateHsnSummary`
 * carries the grouping and the decimal-safe summing; this component is only
 * the period picker and the render.
 */
export function HsnSummary() {
  const data = useDataSource();
  const [from, setFrom] = useState(() => gstDefaultPeriod().from);
  const [to, setTo] = useState(() => gstDefaultPeriod().to);

  const query = useQuery({
    queryKey: ["gst", "hsn-summary", from, to],
    queryFn: () =>
      data.invoicing.list({
        filter: {
          kind: "group" as const,
          combinator: "and" as const,
          children: [
            {
              kind: "condition" as const,
              field: "invoiceDate",
              operator: "gte" as const,
              value: from,
            },
            {
              kind: "condition" as const,
              field: "invoiceDate",
              operator: "lte" as const,
              value: to,
            },
          ],
        },
        // The fixture never carries more than 120 invoices, so one page at
        // the adapter's max is the whole period. A real API backing a wider
        // date range would have to page through every matching invoice, or
        // aggregate server-side, before this report could be trusted at
        // scale — this UI does not attempt that yet.
        limit: 200,
      }),
  });

  const rows = useMemo(
    () => (query.data ? aggregateHsnSummary(query.data.items) : []),
    [query.data],
  );

  return (
    <div className="flex h-full min-h-0 flex-col gap-4">
      <div className="flex shrink-0 flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <Label htmlFor="hsn-summary-period">Period</Label>
          <DateRangePicker
            id="hsn-summary-period"
            from={from}
            to={to}
            onChange={(range) => {
              setFrom(range.from);
              setTo(range.to);
            }}
          />
        </div>
      </div>

      {query.isPending ? (
        <TableSkeleton />
      ) : query.isError ? (
        <LoadFailed
          message={query.error.message}
          onRetry={() => {
            void query.refetch();
          }}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No issued invoices in this period"
          description="Widen the date range, or check back once invoices are issued."
        />
      ) : (
        <div className="min-h-0 flex-1 overflow-auto rounded-md border">
          <table className="w-full border-collapse text-sm">
            <thead className="bg-background sticky top-0 z-10">
              <tr className="border-b text-left">
                <th className="px-3 py-2 font-medium">HSN code</th>
                <th className="px-3 py-2 text-right font-medium">Rate</th>
                <th className="px-3 py-2 text-right font-medium">Qty</th>
                <th className="px-3 py-2 text-right font-medium">
                  Taxable value
                </th>
                <th className="px-3 py-2 text-right font-medium">CGST</th>
                <th className="px-3 py-2 text-right font-medium">SGST</th>
                <th className="px-3 py-2 text-right font-medium">IGST</th>
                <th className="px-3 py-2 text-right font-medium">Cess</th>
                <th className="px-3 py-2 text-right font-medium">Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={`${row.hsnCode}-${row.gstRate}`}
                  className="border-b last:border-b-0"
                >
                  <td className="px-3 py-1.5 tabular-nums">{row.hsnCode}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    {row.gstRate}%
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    {row.quantity}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    {formatMoney(row.taxableValue)}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    {formatMoney(row.cgstAmount)}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    {formatMoney(row.sgstAmount)}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    {formatMoney(row.igstAmount)}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    {formatMoney(row.cessAmount)}
                  </td>
                  <td className="px-3 py-1.5 text-right font-medium tabular-nums">
                    {formatMoney(row.total)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
